import { SpecialOfferLandingSettings } from "../types";

export type SpecialOfferLandingProps = {
  onClose: (specialOffersList: string[]) => void;
  isOpened: boolean;
  data: SpecialOfferLandingSettings[];
  clientInn?: string;
  dataTestId?: string;
  analyticsPageName?: string;
};

/**
 * Типы для работы с полями лендинга.
 * Эти поля приходят в формате JSON с бека, и парсятся на фронте.
 */

export enum BlockTypeEnum {
  TEXT_BLOCK = "TEXT_BLOCK",
  IMAGE_TEXT_BLOCK = "IMAGE_TEXT_BLOCK",
  STEPS_BLOCK = "STEPS_BLOCK",
  TABS_BLOCK = "TABS_BLOCK",
}

export enum TextBlockItemTypeEnum {
  SIMPLE_TEXT = "SIMPLE_TEXT",
  STRUCTURED_TEXT = "STRUCTURED_TEXT",
}

export type TextBlockItemSimple = {
  /**
   * UUID для корректного рендеринга списков.
   */
  id: string;

  /**
   * Тип текстового блока
   */
  type: TextBlockItemTypeEnum.SIMPLE_TEXT;

  /**
   * Название блока (таба) (при наличии)
   */
  title?: string;

  /**
   * Текст текстового блока
   */
  text: string;
};

export type TextBlockStructuredItemElement = {
  /**
   * UUID для корректного рендеринга списков.
   */
  id: string;

  /**
   * Заголовок текстового элемента
   */
  title: string;

  /**
   * Текст текстового элемента
   */
  text: string;
};

export type TextBlockItemStructured = {
  /**
   * UUID для корректного рендеринга списков.
   */
  id: string;

  /**
   * Тип текстового блока
   */
  type: TextBlockItemTypeEnum.STRUCTURED_TEXT;

  /**
   * Название блока (таба) (при наличии)
   */
  title?: string;

  /**
   * Элементы структурированного блока
   * Элементов может быть любое количество
   */
  elements: TextBlockStructuredItemElement[];
};

export type TextBlockItem = TextBlockItemSimple | TextBlockItemStructured;

export type TextBlockType = {
  /**
   * UUID для корректного рендеринга списков.
   */
  id: string;

  /**
   * Тип блока
   */
  type: BlockTypeEnum.TEXT_BLOCK;

  /**
   * Заголовок текстового блока
   */
  blockTitle?: string;

  /**
   * Текстовые блоки
   */
  content: TextBlockItem;
};

export type TabsBlockType = {
  /**
   * UUID для корректного рендеринга списков.
   */
  id: string;

  /**
   * Тип блока
   */
  type: BlockTypeEnum.TABS_BLOCK;

  /**
   * Заголовок блока с табами
   */
  blockTitle?: string;

  /**
   * Описание текстовых блоков на табах
   * Табов (вкладок) может быть любое количество
   */
  tabs: TextBlockItem[];
};

export enum ImageTextBlockTypeEnum {
  HORIZONTAL = "HORIZONTAL",
  VERTICAL = "VERTICAL",
}

export enum ImageTextBlockSizeEnum {
  BIG = "BIG",
  LITTLE = "LITTLE",
}

export type ImageTextBlockItem = {
  /**
   * UUID для корректного рендеринга списков.
   */
  id: string;

  /**
   * Заголовок текстового элемента
   */
  title: string;

  /**
   * Текст текстового элемента
   */
  text: string;

  /**
   * Иллюстрация текстового элемента
   */
  image?: Image;
};

export type Image = {
  /**
   * UUID изображения
   */
  id: string;

  /**
   * Наименование файла изображения
   */
  name: string;
};

export type ImageTextBlockType = {
  /**
   * UUID для корректного рендеринга списков.
   */
  id: string;

  /**
   * Тип блока
   */
  type: BlockTypeEnum.IMAGE_TEXT_BLOCK;

  /**
   * Тип элементов в блоке
   */
  blockType: ImageTextBlockTypeEnum;

  /**
   * Размер элементов в блоке(обязателен для верт.элементов)
   */
  blockSize?: ImageTextBlockSizeEnum;

  /**
   * Заголовок текстового блока с изображением
   */
  blockTitle?: string;

  /**
   * Количество элементов в блоке
   *
   * Возможные значения для горизонтальных блоков: 2, 4.
   * Возможные значения для верт. больших блоков: 2, 4.
   * Возможные значения для верт. маленьких блоков: 2, 3.
   */
  blocksCount: number;

  /**
   * Описание элементов текстового блока с изображением
   */
  blocks: ImageTextBlockItem[];
};

export type StepsBlockItem = {
  /**
   * UUID для корректного рендеринга списков.
   */
  id: string;

  /**
   * Заголовок элемента
   */
  title: string;

  /**
   * Текст элемента
   */
  text: string;
};

export type StepsBlockType = {
  /**
   * UUID для корректного рендеринга списков.
   */
  id: string;

  /**
   * Тип блока
   */
  type: BlockTypeEnum.STEPS_BLOCK;

  /**
   * Заголовок блока с шагами
   */
  blockTitle?: string;

  /**
   * Описание элементов блока с шагами
   * В блоке должно быть строго 3 элемента (шага)
   */
  steps: StepsBlockItem[];
};

export type BlockType =
  | TextBlockType
  | ImageTextBlockType
  | StepsBlockType
  | TabsBlockType;
